import AgencyCallCapture from "./AgencyCallCapture";
import { FileSpreadsheet } from "lucide-react";
import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AGENCY_TZ, formatDate } from "../api";
import { useAuth } from "../auth";
import { useApi } from "../hooks";
import { StatusBadge } from "./Ui";
import { RequestView, useMutation } from "./Workflow";

type ActionType = "booked" | "completed" | "call";
type Delivery = "api" | "sheet" | "off";
type Verdict = "no_rows" | "none_credited" | "low" | "ok" | "over";
type Match = { sent: number; credited: number; rate: number | null; verdict: Verdict };
type FeedAction = {
  id: number;
  action_type: ActionType;
  action_name: string;
  customer_id: string;
  location_id: number | null;
  location: string | null;
  sheet_url: string | null;
  sheet_rows: number | null;
  sheet_updated_at: string | null;
  google_synced: boolean;
  /** Google Ads has reported this action's name at least once (any date). */
  ever_reported?: boolean;
  /** When Google Ads last synced this action's account. */
  account_synced_at?: string | null;
  google_status?: "reported" | "not_seen" | "waiting" | "no_action";
  match: Match;
};
type Account = { customer_id: string; account_label: string; location_id: number | null };
type Feed = {
  window: { days: number; lag_days: number };
  actions: FeedAction[];
  last_upload: { uploaded_at: string; source: string | null; file_name: string | null; rows_in_file: number } | null;
  drop_link: { created_at: string; last_used_at: string | null } | null;
  locations: { id: number; market_name: string }[];
  ads_accounts: Account[];
};
type GoogleSetup = { accepted_customer_data_terms?: boolean; enhanced_conversions_for_leads_enabled?: boolean; checked_at?: string } | null;
type SyncAction = {
  id: number;
  action_type: ActionType;
  action_name: string;
  customer_id: string;
  location: string | null;
  delivery: Delivery;
  conversion_action_id: string | null;
  google_setup: GoogleSetup;
  last_build: { at: string; eligible: number; pending: number } | null;
  min_call_seconds: number | null;
  counts: { pending: number; sent: number; accepted: number; failed: number };
  held: number;
  last_sent_at: string | null;
  last_error: string | null;
  last_request: { request_id: string; created_at: string; status: string; events: number } | null;
  credited_30d: number | null;
  credited_value_30d: number | null;
};
type SyncStatus = {
  google: { scope_ok: boolean; login_customer_id_set: boolean };
  /** NOLA's own CRM bookings and won deals: counted in this client's Google Ads, in another client's (its name), or off. */
  crm?: { here: boolean; elsewhere: string | null };
  schedules_on: boolean;
  actions: SyncAction[];
};
type SheetResult = {
  action_type: string;
  action_name: string;
  customer_id: string;
  location: string | null;
  rows?: number;
  excluded?: Record<string, number>;
  identifier_coverage?: Record<string, number>;
  sheet_url?: string;
  sheet_created?: boolean;
  skipped?: string;
  error?: string;
  warning?: string;
  notes?: string[];
};
type SendReport = {
  status: "ok" | "needs_reauth" | "not_configured" | "failed";
  sent?: number;
  failed?: number;
  held?: number;
  validated?: { action_id: number; request_id?: string | null; events?: number; warnings?: unknown[]; error?: string; transaction_id?: string | null }[];
  errors?: { action_id?: number; kind?: string; message: string }[];
  built?: { action_id: number; action_type: ActionType; eligible: number; pending: number }[];
  error?: string;
};
/** What a dry run read from the file (utils/conversionFeed describeFile). */
type FileCheck = {
  rows_read: number;
  error?: string;
  jobs_found?: number;
  first_day?: string | null;
  last_day?: string | null;
  national_accounts?: number;
  actions?: { action_id: number; action_type: ActionType; action_name: string; location: string | null; delivery: Delivery; rows: number | null; error?: string }[];
};
type RunResult = {
  dry_run: boolean;
  job_history: { jobs_new?: number; jobs_updated?: number; error?: string } | null;
  matching: { matched?: number; jobs?: number; error?: string } | null;
  sheets: SheetResult[];
  sync: SendReport | null;
  check?: FileCheck;
};
type ConnectResult = {
  accounts: {
    customer_id: string;
    label: string;
    skipped?: string;
    error?: string;
    setup?: { accepted_customer_data_terms: boolean; enhanced_conversions_for_leads_enabled: boolean };
    actions: { action_type: ActionType; created?: boolean; delivery?: Delivery; not_found?: boolean; note?: string; error?: string }[];
  }[];
};
type Tone = "neutral" | "success" | "warning" | "danger" | "info";

const MAX_BYTES = 4.4 * 1024 * 1024; // Vercel refuses a body over 4.5 MB, file plus form overhead
const SOURCE: Record<string, string> = { drop_link: "the drop link", staff: "a staff upload", generate: "a staff upload" };
const DEFAULT_NAME: Record<ActionType, string> = { booked: "NL-AOS Booked", completed: "NL-AOS Completed", call: "NL-AOS Qualified call" };
const TYPE_LINE: Record<ActionType, string> = { booked: "Every booking", completed: "Completed jobs, with value", call: "Qualified calls" };
const DELIVERY_LABEL: Record<Delivery, string> = { api: "Direct to Google Ads", sheet: "Google Sheet", off: "Off" };
const ADMIN_ONLY = "Only an admin can do this";

/** Google Ads shows account ids as 123-456-7890. */
const accountId = (id: string) => (/^\d{10}$/.test(id) ? `${id.slice(0, 3)}-${id.slice(3, 6)}-${id.slice(6)}` : id);
const daysAgo = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
const ago = (iso: string) => {
  const d = daysAgo(iso);
  return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
};
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
/** "Oct 2" for a moment, on the agency's calendar. */
const shortDay = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: AGENCY_TZ });
/** "Jul 20 to Aug 18" for two calendar days written YYYY-MM-DD. */
const dayRange = (from: string, to: string) => {
  const d = (ymd: string) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", ...(from.slice(0, 4) !== to.slice(0, 4) ? { year: "numeric" } : {}), timeZone: "UTC" });
  return from === to ? d(from) : `${d(from)} to ${d(to)}`;
};

/** Google synced the account and still reports nothing for this action: say what to fix. */
function notSeen(a: FeedAction | undefined, sheetFed: boolean): string | null {
  if (!a || a.google_status !== "not_seen" || !a.account_synced_at) return null;
  return `Google Ads synced this account on ${shortDay(a.account_synced_at)} but reports no ${a.action_name} conversions.${sheetFed ? " Make the Data Manager connection or switch to Direct." : ""}`;
}

/** What a sheet action needs next, in plain words. */
function sheetState(a: FeedAction | undefined, days: number): { tone: Tone; label: string; hint: string } {
  if (!a || !a.sheet_url) return { tone: "neutral", label: "No sheet yet", hint: "Upload an export to create it." };
  if (!a.google_synced) {
    const unseen = notSeen(a, true);
    if (unseen) return { tone: "warning", label: "Not seen in Google", hint: unseen };
    if (a.ever_reported) return { tone: "neutral", label: "Nothing credited lately", hint: `Google credited nothing in the last ${days} days.` };
    return { tone: "neutral", label: "Waiting for Google", hint: "Shows after the next Google Ads sync." };
  }
  const m = a.match;
  if (m.verdict === "no_rows") return { tone: "neutral", label: "Nothing sent lately", hint: `No rows in the last ${days} days.` };
  if (m.verdict === "none_credited") return { tone: "danger", label: `0 of ${m.sent} credited`, hint: "Check the Data Manager connection and the action name." };
  if (m.verdict === "low") return { tone: "warning", label: `${m.rate}% credited`, hint: `${m.credited} of ${m.sent}. Under 15% usually means a column mapping problem.` };
  if (m.verdict === "over") return { tone: "info", label: `${m.rate}% credited`, hint: "More than was sent: something else also feeds this action." };
  return { tone: "success", label: `${m.rate}% credited`, hint: `${m.credited} of ${m.sent}. 30 to 60% is normal.` };
}

/** The Google switches an api action depends on for hashed rows. */
function googleFlags(a: SyncAction, isAdmin: boolean): { tone: Tone; label: string; hint: string } {
  const s = a.google_setup;
  if (!s || s.accepted_customer_data_terms === undefined) return { tone: "neutral", label: "Not checked yet", hint: isAdmin ? "Press Connect, or wait for the nightly sync." : "The nightly sync checks it." };
  if (s.accepted_customer_data_terms && s.enhanced_conversions_for_leads_enabled) return { tone: "success", label: "Google switches on", hint: s.checked_at ? `Checked ${ago(s.checked_at)}` : "" };
  return { tone: "warning", label: "Turn on enhanced conversions", hint: `In Google Ads: Goals → Conversions → Settings. ${!s.accepted_customer_data_terms ? "Accept the customer data terms. " : ""}${!s.enhanced_conversions_for_leads_enabled ? "Turn on enhanced conversions for leads." : ""}${a.held ? ` ${plural(a.held, "conversion")} waiting on it.` : ""} Then ${isAdmin ? "press Connect to re-check (the sender also re-checks once a day)" : "the sender re-checks once a day"}.` };
}

function freshness(iso: string | undefined): { tone: Tone; label: string } {
  if (!iso) return { tone: "danger", label: "None yet" };
  const d = daysAgo(iso);
  return d <= 3 ? { tone: "success", label: "Current" } : d <= 7 ? { tone: "warning", label: "Due" } : { tone: "danger", label: "Stale" };
}

/** One sentence for a send report. */
function sendSentence(r: SendReport | null | undefined, verb = "Sent"): string {
  if (!r) return "";
  if (r.status === "needs_reauth") return "Google needs reconnecting before anything can be sent: Settings → Integrations → Google Workspace → Reconnect Google, then allow Data Manager.";
  if (r.status === "not_configured") return "The Google Ads manager account is not set up yet, so nothing was sent.";
  if (r.status === "failed") return r.error || "Conversions were queued but not sent yet; the next cron run retries.";
  const bits = [`${verb} ${plural(r.sent ?? 0, "conversion")}`];
  if (r.failed) bits.push(`${r.failed} failed`);
  if (r.held) bits.push(`${r.held} waiting on the Google switches`);
  if (r.errors?.length) bits.push(r.errors[0].message);
  return bits.join(", ") + ".";
}

/**
 * The booked-job → Google Ads loop for one client: Google connection and the
 * nightly syncs, one row per conversion action with its delivery mode and queue,
 * the upload, the drop link, and a manual add for the odd account. `onChanged`
 * tells the page when job data or actions changed, so its numbers and checklist
 * catch up without a reload.
 */
export default function ConversionFeed({ clientId, onChanged }: { clientId: number; onChanged?: () => void }) {
  const { user } = useAuth();
  const canEdit = user?.role === "superadmin" || user?.role === "exec";
  const isAdmin = user?.role === "superadmin";
  const feed = useApi<Feed>(`/api/conversions/${clientId}/feed`);
  const sync = useApi<SyncStatus>(`/api/conversions/${clientId}/sync`);
  // Each area reports beside its own controls: the actions table, the upload, the drop link, the manual add.
  const syncMutation = useMutation();
  const uploadMutation = useMutation();
  const linkMutation = useMutation();
  const addMutation = useMutation();
  // Which button started the running action, so only that one says it is working.
  const [running, setRunning] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState<RunResult | null>(null);
  const [connected, setConnected] = useState<ConnectResult | null>(null);
  const [validated, setValidated] = useState<SendReport | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const f = feed.data;
  const s = sync.data;
  const days = f?.window.days ?? 30;
  const last = f?.last_upload;
  const fresh = freshness(last?.uploaded_at);
  const feedById = new Map((f?.actions || []).map((a) => [a.id, a]));
  const actions = s?.actions || [];
  const apiActions = actions.filter((a) => a.delivery === "api");
  const sheetActions = actions.filter((a) => a.delivery === "sheet");
  const totals = actions.reduce((t, a) => ({ pending: t.pending + a.counts.pending, sent: t.sent + a.counts.sent + a.counts.accepted, failed: t.failed + a.counts.failed }), { pending: 0, sent: 0, failed: 0 });
  const lastSent = actions.map((a) => a.last_sent_at).filter((x): x is string => !!x).sort().at(-1);
  const credited = actions.reduce((n, a) => n + (a.credited_30d ?? 0), 0);
  const anyCredited = actions.some((a) => a.credited_30d != null);
  // One action Google synced and still never reported, for the Credited tile. Only
  // an action that gave Google something (a sheet, or a send) can be missing there.
  const unseen = actions.map((a) => {
    const fa = feedById.get(a.id);
    if (a.delivery === "sheet") return fa?.sheet_url ? notSeen(fa, true) : null;
    if (a.delivery === "api") return a.counts.sent + a.counts.accepted > 0 ? notSeen(fa, false) : null;
    return null;
  }).find(Boolean) || null;
  // Uploads, sends and connects all write the same queue: one at a time.
  const busy = syncMutation.busy || uploadMutation.busy || linkMutation.busy || addMutation.busy;
  const reloadAll = () => { feed.reload(); sync.reload(); };
  const changed = () => { reloadAll(); onChanged?.(); };
  const working = (name: string, idle: string, active: string) => (running === name && busy ? active : idle);

  const send = async (dryRun: boolean) => {
    const file = fileRef.current?.files?.[0];
    if (!file) return uploadMutation.setNotice("Choose the job export first.");
    if (file.size > MAX_BYTES) return uploadMutation.setNotice("That file is over 4.4 MB. Export a shorter date range (the last three months is plenty).");
    const form = new FormData();
    form.append("appointment_file", file);
    if (dryRun) form.append("dry_run", "true");
    setRunning(dryRun ? "check" : "upload");
    setResult(null);
    const r = await uploadMutation.run<RunResult>(`/api/conversions/${clientId}/feed`, form);
    if (r) {
      setResult(r);
      if (!dryRun) changed();
    }
  };
  const connect = async () => {
    setRunning("connect");
    const r = await syncMutation.run<ConnectResult>(`/api/conversions/${clientId}/connect`, {});
    if (r) {
      setConnected(r);
      const made = r.accounts.flatMap((a) => a.actions).filter((x) => x.created).length;
      const errors = r.accounts.filter((a) => a.error).length;
      syncMutation.setNotice(`${made ? `Created ${plural(made, "conversion action")} in Google Ads. ` : "Every account already had its actions. "}${errors ? `${plural(errors, "account")} could not be read; see below.` : ""}`.trim());
      changed();
    }
  };
  const sendNow = async () => {
    setRunning("send");
    const r = await syncMutation.run<SendReport>(`/api/conversions/${clientId}/sync/run`, {});
    if (r) { syncMutation.setNotice(sendSentence(r)); changed(); }
  };
  const validate = async () => {
    setRunning("validate");
    const r = await syncMutation.run<SendReport>(`/api/conversions/${clientId}/sync/validate`, {});
    if (r) {
      setValidated(r);
      const okCount = (r.validated || []).filter((v) => !v.error).length;
      const bad = (r.validated || []).filter((v) => v.error).length;
      syncMutation.setNotice(r.status === "ok" ? `Google checked ${plural(okCount, "batch", "batches")} with no objection${bad ? ` and rejected ${bad}` : ""}. Nothing was sent.` : sendSentence(r));
      sync.reload();
    }
  };
  const retry = async () => {
    setRunning("retry");
    const r = await syncMutation.run<{ retried: number }>(`/api/conversions/${clientId}/sync/retry`, {});
    if (r) { syncMutation.setNotice(`${plural(r.retried, "conversion")} back on the queue.`); sync.reload(); }
  };
  const setDelivery = async (a: SyncAction, delivery: Delivery) => {
    if (a.delivery === "sheet" && delivery === "api") {
      if (!/^\d+$/.test(String(a.conversion_action_id ?? ""))) return syncMutation.setNotice("Press Connect first so this action has its Google Ads id.");
      // What switching means for the sheet Google still reads, asked once, where it applies.
      if (!window.confirm(`Send "${a.action_name}" direct to Google Ads?\n\nGoogle de-duplicates on the job code, so nothing counts twice. The sheet stops being rewritten, but its Data Manager connection keeps reading it: once the first direct send is credited, remove that connection in Google Ads (Tools → Data Manager) so a stale sheet cannot overwrite corrected values.`)) return;
    }
    setRunning("delivery");
    const r = await syncMutation.run(`/api/conversions/${clientId}/actions/${a.id}`, { delivery }, "PUT");
    if (r) changed();
  };
  // NOLA's own sales: CRM bookings and won deals count in this account (one client at a time).
  const setCrm = async (on: boolean) => {
    if (on && s?.crm?.elsewhere && !window.confirm(`NOLA's own bookings and won deals count in ${s.crm.elsewhere}'s Google Ads now. Count them here instead?`)) return;
    setRunning("crm");
    const r = await syncMutation.run(`/api/conversions/${clientId}/crm`, { on }, "PUT");
    if (r) { syncMutation.setNotice(on ? "Calls booked and deals won in the CRM now count here. New ones go to Google within five minutes." : "CRM bookings and won deals no longer count here."); changed(); }
  };
  const crmReady = apiActions.some((a) => !a.location && (a.action_type === "booked" || a.action_type === "completed"));
  const makeLink = async () => {
    if (f?.drop_link && !window.confirm("Replace the drop link? The current one stops working straight away, so anyone using it needs the new one.")) return;
    setRunning("link");
    const r = await linkMutation.run<{ url: string }>(`/api/conversions/${clientId}/drop-link`, {});
    if (r) { setLink(r.url); feed.reload(); }
  };
  const copy = (target: ReturnType<typeof useMutation>) => async (text: string, what: string) => {
    try { await navigator.clipboard.writeText(text); target.setNotice(`${what} copied.`); }
    catch { target.setNotice("Copy failed. Select it and copy it by hand."); }
  };
  const retire = async (a: SyncAction) => {
    if (!window.confirm(`Stop feeding "${a.action_name}" for account ${accountId(a.customer_id)}? Its queue and sheet stay, but nothing more is sent.`)) return;
    setRunning("retire");
    const r = await syncMutation.run(`/api/conversions/${clientId}/actions/${a.id}`, { active: false }, "PUT");
    if (r) changed();
  };

  const googleState: { tone: Tone; label: string; hint: ReactNode; title?: string } = !s
    ? { tone: "neutral", label: "—", hint: "" }
    // A one-time admin setup step, not an error: no red, and no setting name for everyone to read.
    : !s.google.login_customer_id_set ? { tone: "warning", label: "Not set up yet", hint: <>The Google Ads manager account is not set up yet. An admin sets this up once in <Link to="/settings#integrations">Settings, Integrations</Link>.</>, title: isAdmin ? "GOOGLE_ADS_LOGIN_CUSTOMER_ID is not set." : undefined }
    : s.google.scope_ok ? { tone: "success", label: "Ready", hint: "Sends go straight to each account's conversion actions." }
    : { tone: "danger", label: "Reconnect Google", hint: `Settings → Integrations → Google Workspace → Reconnect Google, then allow Data Manager. One new permission is needed.${isAdmin ? "" : " An admin does this."}` };

  // How jobs reach Google depends on each action's delivery: say only what applies.
  const intro = apiActions.length && !sheetActions.length
    ? " Direct actions need nothing in Google Ads Data Manager. A job that changes (a corrected total, a click ID matched later) is sent again and Google updates it in place."
    : sheetActions.length
      ? " Sheet actions reach Google through a Data Manager connection made once in Google Ads; direct actions need nothing there."
      : "";
  const connectButton = (primary: boolean) => isAdmin
    ? <button className={primary ? "button" : "button secondary"} disabled={busy || !(f?.ads_accounts.length)} onClick={() => void connect()}>{working("connect", "Connect Google Ads conversions", "Connecting…")}</button>
    : canEdit
      ? <button className={primary ? "button" : "button secondary"} disabled title={ADMIN_ONLY}>Connect Google Ads conversions</button>
      : null;

  return (
    <section className="panel conversion-feed" id="conversion-sync" aria-labelledby="conversion-feed-title">
      <div className="panel-heading">
        <div>
          <h2 id="conversion-feed-title">Conversion sync</h2>
        </div>
        <div className="feed-actions-row">
          {canEdit && apiActions.length > 0 && (
            <>
              <button className="button" disabled={busy} onClick={() => void sendNow()}>{working("send", "Send now", "Sending…")}</button>
              <button className="button secondary" disabled={busy} onClick={() => void validate()}>{working("validate", "Validate only", "Checking…")}</button>
            </>
          )}
          {canEdit && totals.failed > 0 && <button className="button secondary" disabled={busy} onClick={() => void retry()}>{working("retry", "Retry failed", "Retrying…")}</button>}
          {actions.length > 0 && connectButton(false)}
          <button className="button secondary" onClick={reloadAll}>Refresh</button>
        </div>
      </div>
      <p className="workflow-note">
        Booked and completed jobs go to each Google Ads account's conversion actions, matched by click ID or by
        hashed email, phone and name. National Account jobs are left out.{intro}
      </p>
      {syncMutation.feedback}
      <RequestView request={sync}>
        <dl className="feed-status feed-status-4">
          <div>
            <dt>Google connection</dt>
            <dd><StatusBadge tone={googleState.tone}>{googleState.label}</StatusBadge></dd>
            <span title={googleState.title}>{googleState.hint}</span>
          </div>
          <div>
            <dt>Nightly syncs</dt>
            <dd><StatusBadge tone={s?.schedules_on ? "success" : "warning"}>{s?.schedules_on ? "On" : "Off"}</StatusBadge></dd>
            <span>
              {s?.schedules_on
                ? "Ads spend, credited counts and matches refresh every night"
                : isAdmin ? <>Turn them on in <Link to="/conversions">Portfolio → Conversions</Link></> : "Turned on in Portfolio → Conversions (an admin does this)"}
            </span>
          </div>
          <div>
            <dt>Queue</dt>
            <dd>
              <strong>{totals.pending.toLocaleString()}</strong>
              <span className="feed-queue">
                {totals.sent > 0 && <StatusBadge tone="success">{`${totals.sent.toLocaleString()} sent`}</StatusBadge>}
                {totals.failed > 0 && <StatusBadge tone="danger">{`${totals.failed.toLocaleString()} failed`}</StatusBadge>}
              </span>
            </dd>
            <span>{lastSent ? `Waiting to go. Last send ${ago(lastSent)}.` : apiActions.length ? "Waiting to go. Nothing sent yet." : "No direct actions yet"}</span>
          </div>
          <div>
            <dt>Credited by Google, last 30 days</dt>
            <dd><strong>{anyCredited ? credited.toLocaleString() : "—"}</strong></dd>
            <span>
              {anyCredited
                ? "All actions. Appears a day or two after a send."
                : !actions.length ? "No conversion actions yet" : unseen ? "Not reported by Google yet. What to do is on its action below." : "Appears after the next Google Ads sync"}
            </span>
          </div>
        </dl>

        {actions.length ? (
          <div className="table-wrap">
            <table className="workflow-table feed-table">
              <thead>
                <tr>
                  <th scope="col">Territory</th>
                  <th scope="col">Conversion action</th>
                  <th scope="col">Google Ads account</th>
                  <th scope="col">Delivery</th>
                  <th scope="col">Queue</th>
                  <th scope="col">Google</th>
                  {isAdmin && <th scope="col"><span className="sr-only">Actions</span></th>}
                </tr>
              </thead>
              <tbody>
                {actions.map((a) => {
                  const fa = feedById.get(a.id);
                  const st = a.delivery === "sheet" ? sheetState(fa, days) : googleFlags(a, isAdmin);
                  const directUnseen = a.delivery === "api" && a.credited_30d == null && a.counts.sent + a.counts.accepted > 0 ? notSeen(fa, false) : null;
                  return (
                    <tr key={a.id}>
                      <td data-label="Territory">{a.location || "Whole client"}</td>
                      <td data-label="Conversion action">
                        <strong>{a.action_name}</strong>
                        <span className="feed-sub">{a.action_type === "call" && a.min_call_seconds ? `CTM calls of ${a.min_call_seconds} s or more on Google numbers` : TYPE_LINE[a.action_type]}{a.conversion_action_id ? `, id ${a.conversion_action_id}` : ""}</span>
                      </td>
                      <td data-label="Google Ads account"><span className="feed-mono">{accountId(a.customer_id)}</span></td>
                      <td data-label="Delivery">
                        {isAdmin ? (
                          <select className="feed-delivery" aria-label={`Delivery for ${a.action_name}`} value={a.delivery} disabled={busy} onChange={(e) => void setDelivery(a, e.target.value as Delivery)}>
                            <option value="api">Direct to Google Ads</option>
                            <option value="sheet">Google Sheet</option>
                            <option value="off">Off</option>
                          </select>
                        ) : <span>{DELIVERY_LABEL[a.delivery]}</span>}
                        {a.delivery === "sheet" && fa?.sheet_url && (
                          <span className="feed-sub"><a href={fa.sheet_url} target="_blank" rel="noreferrer">{plural(fa.sheet_rows ?? 0, "sheet row")}</a>, updated {formatDate(fa.sheet_updated_at)}</span>
                        )}
                        {a.delivery === "sheet" && !fa?.sheet_url && <span className="feed-sub">Sheet not created yet</span>}
                        {a.delivery === "api" && !/^\d+$/.test(String(a.conversion_action_id ?? "")) && <span className="feed-sub">Needs its Google Ads id: {isAdmin ? "press Connect" : "an admin presses Connect"}</span>}
                        {a.delivery === "api" && fa?.sheet_url && (
                          <span className="feed-sub">Was sheet-fed: remove this action's Data Manager connection in Google Ads. The <a href={fa.sheet_url} target="_blank" rel="noreferrer">old sheet</a> is no longer updated, but Google keeps reading it until then.</span>
                        )}
                      </td>
                      <td data-label="Queue">
                        {a.delivery === "api" ? (
                          <>
                            <span className="feed-queue">
                              <StatusBadge tone={a.counts.pending ? "info" : "neutral"}>{`${a.counts.pending} pending`}</StatusBadge>
                              <StatusBadge tone={a.counts.sent + a.counts.accepted ? "success" : "neutral"}>{`${a.counts.sent + a.counts.accepted} sent`}</StatusBadge>
                              {a.counts.failed > 0 && <StatusBadge tone="danger">{`${a.counts.failed} failed`}</StatusBadge>}
                            </span>
                            <span className="feed-sub">
                              {a.held ? `${plural(a.held, "row")} waiting on the Google switches. ` : ""}
                              {a.last_sent_at ? `Last send ${ago(a.last_sent_at)}.` : a.last_build ? `Built ${ago(a.last_build.at)}, ${plural(a.last_build.eligible, "eligible job")}.` : "Nothing built yet."}
                            </span>
                          </>
                        ) : <span className="feed-sub">{a.delivery === "sheet" ? "Google reads the sheet on its own schedule" : "Not sending"}</span>}
                      </td>
                      <td data-label="Google">
                        <span className="feed-badge"><StatusBadge tone={st.tone}>{st.label}</StatusBadge></span>
                        <span className="feed-sub">{a.delivery === "api" && a.credited_30d != null ? `${a.credited_30d.toLocaleString()} credited in 30 days. ` : ""}{st.hint}{directUnseen ? ` ${directUnseen}` : ""}</span>
                      </td>
                      {isAdmin && (
                        <td className="feed-row-actions">
                          <button className="button secondary" disabled={busy} onClick={() => void retire(a)} aria-label={`Retire ${a.action_name} for ${a.location || "the whole client"}`}>Retire</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="feed-empty">
            <strong>No conversion actions yet</strong>
            <p>
              {f?.ads_accounts.length
                ? `Press "Connect Google Ads conversions". It creates "NL-AOS Booked" and "NL-AOS Completed" in each linked Google Ads account and starts sending as soon as job data lands.${isAdmin ? "" : " An admin does this."}`
                : isAdmin
                  ? <>Link the client's Google Ads account in <Link to={`/client-settings?client_id=${clientId}`}>Setup → Connections</Link> first, then press Connect here.</>
                  : "Link the client's Google Ads account in Setup → Connections first, then press Connect here (an admin does this)."}
            </p>
            {f?.ads_accounts.length ? connectButton(true) : null}
          </div>
        )}
        {actions.some((a) => a.last_error) && (
          <ul className="tracking-notes feed-errors">
            {actions.filter((a) => a.last_error).map((a) => (
              <li key={a.id}><strong>{a.location ? `${a.location}, ` : ""}{a.action_name}:</strong> {a.last_error}</li>
            ))}
          </ul>
        )}
      </RequestView>

      {connected && <ConnectSummary result={connected} />}
      {validated && validated.status === "ok" && <ValidateSummary report={validated} actions={actions} />}

      {isAdmin && <AgencyCallCapture />}
      {(crmReady || s?.crm?.here) && (
        <div className="feed-section" id="conversion-crm">
          <div className="feed-section-head">
            <div>
              <h3>NOLA's own sales</h3>
              <p className="feed-sub">
                {s?.crm?.here
                  ? crmReady
                    ? "Calls booked and deals won in the CRM count here as NL-AOS Booked and NL-AOS Completed (with the deal amount), matched by the visitor's ad click or hashed email and phone."
                    : "This is the selected destination for NOLA's own sales. Connect this client's Google Ads account and conversion actions before CRM conversions can be sent."
                  : s?.crm?.elsewhere
                    ? `Calls booked and deals won in the CRM count in ${s.crm.elsewhere}'s Google Ads.`
                    : "Calls booked and deals won in the CRM don't count in any Google Ads account yet. Turn this on for the account that runs NOLA's own ads."}
              </p>
            </div>
            {isAdmin && (
              <button className="button secondary" disabled={busy} onClick={() => void setCrm(!s?.crm?.here)}>
                {working("crm", s?.crm?.here ? "Stop counting here" : "Count them here", "Saving…")}
              </button>
            )}
          </div>
        </div>
      )}

      {canEdit && (
        <div className="feed-upload-area" id="conversion-upload">
          {uploadMutation.feedback}
          <div className="feed-upload">
            <label className="callcenter-drop">
              <span className="callcenter-drop-icon" aria-hidden="true"><FileSpreadsheet /></span>
              <strong>Job or appointment export</strong>
              <span>.xlsx or .csv, up to 4.4 MB</span>
              <span className="button secondary">Choose file</span>
              <small>{fileName || "No file selected"}</small>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { setFileName(e.target.files?.[0]?.name || ""); setResult(null); }} />
            </label>
            <div className="feed-upload-actions">
              <button className="button" disabled={busy || !fileName} onClick={() => void send(false)}>
                {working("upload", "Upload export", "Uploading…")}
              </button>
              <button className="button secondary" disabled={busy || !fileName} onClick={() => void send(true)}>{working("check", "Check without sending", "Checking…")}</button>
              <p className="feed-sub">
                {!fileName
                  ? "Choose a file to continue."
                  : "Job history is updated, matches rebuilt, and conversions queued and sent. Sheet-fed actions are rewritten too. Google skips rows it already has."}
              </p>
            </div>
          </div>
        </div>
      )}

      {result && <FeedResult result={result} onCopy={copy(uploadMutation)} />}

      <div className="feed-section">
        <div className="feed-section-head">
          <div>
            <h3>Drop link</h3>
            <p className="feed-sub">
              {f?.drop_link
                ? `Made ${formatDate(f.drop_link.created_at)}, ${f.drop_link.last_used_at ? `last used ${ago(f.drop_link.last_used_at)}` : "not used yet"}.`
                : "Not made yet."}
              {last ? ` Last export ${ago(last.uploaded_at)} via ${SOURCE[last.source || ""] || "an upload"}, ${plural(last.rows_in_file, "row")} in the file (${fresh.label.toLowerCase()}).` : ""}
            </p>
          </div>
          {canEdit && (
            <button className="button secondary" disabled={busy} onClick={() => void makeLink()}>
              {working("link", f?.drop_link ? "Replace link" : "Make link", "Making…")}
            </button>
          )}
        </div>
        {linkMutation.feedback}
        <p className="workflow-note">
          A private upload page for this client, with no login. Franchise staff pick the file. Zapier, Make or an email parser
          can post it to the same address (field <code>appointment_file</code>), so a scheduled report email feeds Google on its own.
        </p>
        {link && (
          <div className="tracking-site tracking-site-new" role="status">
            <strong>Copy it now. It is not shown again.</strong>
            <div className="tracking-snippet">
              <code>{link}</code>
              <button className="button" onClick={() => void copy(linkMutation)(link, "Link")}>Copy</button>
            </div>
          </div>
        )}
      </div>

      {isAdmin && f && (
        <details className="callcenter-options feed-add" open={adding} onToggle={(e) => setAdding((e.target as HTMLDetailsElement).open)}>
          <summary>Add a conversion action by hand</summary>
          <AddAction
            accounts={f.ads_accounts}
            locations={f.locations}
            busy={busy}
            onSave={async (body) => {
              setRunning("add");
              const r = await addMutation.run(`/api/conversions/${clientId}/actions`, body, "PUT");
              if (r) {
                addMutation.setNotice(body.conversion_action_id || body.action_type === "call" ? "Added. It sends direct to Google Ads from the next upload." : "Added as a sheet action. Upload an export to create its sheet, then connect the sheet in Data Manager.");
                setAdding(false);
                changed();
              }
            }}
          />
        </details>
      )}
      {/* Under the fold, so the notice stays in view after the form folds away on a save. */}
      {isAdmin && <div className="feed-add-feedback">{addMutation.feedback}</div>}
    </section>
  );
}

function ConnectSummary({ result }: { result: ConnectResult }) {
  return (
    <div className="conversion-feed-result" role="status">
      <p><strong>Connect finished.</strong> One line per Google Ads account.</p>
      <ul>
        {result.accounts.map((a) => (
          <li key={a.customer_id} className={a.error ? "danger" : ""}>
            <strong>{a.label} ({accountId(a.customer_id)})</strong>{" "}
            {a.skipped === "lsa"
              ? "skipped: a Local Services account takes no imported conversions."
              : a.error
                ? a.error
                : <>
                    {a.actions.map((x) => x.not_found ? x.note : x.error ? `${DEFAULT_NAME[x.action_type]}: ${x.error}` : `${DEFAULT_NAME[x.action_type]} ${x.created ? "created" : "already there"}${x.delivery === "sheet" ? " (still sheet-fed)" : ""}`).join(" ")}
                    {a.setup && !(a.setup.accepted_customer_data_terms && a.setup.enhanced_conversions_for_leads_enabled) && (
                      <span className="conversion-feed-next">Enhanced conversions for leads is not on in this account yet. Click-ID rows send anyway; hashed rows wait.</span>
                    )}
                  </>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ValidateSummary({ report, actions }: { report: SendReport; actions: SyncAction[] }) {
  const name = (id: number) => { const a = actions.find((x) => x.id === id); return a ? `${a.location ? `${a.location}, ` : ""}${a.action_name}` : `Action ${id}`; };
  return (
    <div className="conversion-feed-result" role="status">
      <p><strong>{report.status === "ok" ? "Google checked the queue. Nothing was sent." : sendSentence(report)}</strong></p>
      {report.status === "ok" && (
        <ul>
          {(report.validated || []).map((v, i) => (
            <li key={i} className={v.error ? "danger" : ""}>
              <strong>{name(v.action_id)}:</strong>{" "}
              {v.error
                ? `${v.transaction_id ? `job ${v.transaction_id} ` : ""}rejected: ${v.error}`
                : `${plural(v.events ?? 0, "row")} accepted${v.warnings?.length ? `, ${plural(v.warnings.length, "warning")}` : ""}`}
            </li>
          ))}
          {!(report.validated || []).length && <li>Nothing was pending to check.</li>}
        </ul>
      )}
    </div>
  );
}

/** What "Check without sending" read from the file. Nothing was saved or sent. */
function CheckResult({ check }: { check: FileCheck }) {
  if (check.error) {
    return (
      <div className="conversion-feed-result" role="status">
        <p><strong>This file's columns were not recognised.</strong> Nothing was saved or sent. Upload the job or appointment export as it came out of the system.</p>
      </div>
    );
  }
  const actions = check.actions || [];
  return (
    <div className="conversion-feed-result" role="status">
      <p>
        <strong>
          File reads correctly: {plural(check.rows_read, "row")}, {plural(check.jobs_found ?? 0, "job")}
          {check.first_day && check.last_day ? ` booked ${dayRange(check.first_day, check.last_day)}` : ""}.
        </strong>{" "}
        Nothing was saved or sent.
      </p>
      <ul>
        {!!check.national_accounts && <li>{plural(check.national_accounts, "National Account job")} would be left out.</li>}
        {actions.map((a) => (
          <li key={a.action_id} className={a.error ? "danger" : ""}>
            <strong>{a.location ? `${a.location}, ` : ""}{a.action_name}:</strong>{" "}
            {a.error ? a.error : a.delivery === "api"
              ? `${plural(a.rows ?? 0, "job")} would be sent to Google Ads (jobs Google already has are skipped).`
              : `${plural(a.rows ?? 0, "row")} would go on the sheet.`}
          </li>
        ))}
        {!actions.length && <li>No conversion actions yet. Uploading would store the jobs for reporting.</li>}
      </ul>
    </div>
  );
}

function FeedResult({ result, onCopy }: { result: RunResult; onCopy: (text: string, what: string) => void }) {
  if (result.dry_run && result.check) return <CheckResult check={result.check} />;
  const created = result.sheets.filter((s) => s.sheet_created && s.sheet_url);
  const h = result.job_history;
  return (
    <div className="conversion-feed-result" role="status">
      <p>
        <strong>{result.dry_run ? "Checked. Nothing was saved or sent." : result.sync ? sendSentence(result.sync) : result.sheets.length ? "Sheets updated." : "Saved."}</strong>{" "}
        {h && !h.error && `${plural(h.jobs_new ?? 0, "new job")}, ${(h.jobs_updated ?? 0).toLocaleString()} updated. `}
        {result.matching && !result.matching.error && `${(result.matching.matched ?? 0).toLocaleString()} of ${plural(result.matching.jobs ?? 0, "job")} matched to a call or lead.`}
      </p>
      <ul>
        {h?.error && <li className="danger">{h.error}</li>}
        {result.matching?.error && <li>{result.matching.error}</li>}
        {(result.sync?.built || []).map((b) => (
          <li key={`b${b.action_id}`}>{plural(b.eligible, "eligible job")} for the {b.action_type} action, {b.pending} waiting to send.</li>
        ))}
        {result.sheets.map((s, i) => (
          <li key={i} className={s.error ? "danger" : ""}>
            <strong>{s.location ? `${s.location}, ` : ""}{s.action_name}:</strong>{" "}
            {s.error ? s.error : (
              <>
                {plural(s.rows ?? 0, "row")}{result.dry_run ? " would go" : ""} on the sheet
                {s.excluded?.skipped_national_account ? `, ${s.excluded.skipped_national_account} National Account left out` : ""}
                {s.identifier_coverage?.with_click_id ? `, ${s.identifier_coverage.with_click_id} with a click ID` : ""}
                {s.skipped ? `. ${s.skipped}` : ""}
                {s.sheet_url && <>. <a href={s.sheet_url} target="_blank" rel="noreferrer">Open sheet</a></>}
                {s.warning && <span className="conversion-feed-next">{s.warning}</span>}
              </>
            )}
          </li>
        ))}
        {/* Only a real upload stores job history; a check saves nothing. */}
        {!result.dry_run && !result.sheets.length && !result.sync && <li>No conversion actions yet, so only the job history was updated.</li>}
      </ul>
      {!!created.length && (
        <div className="feed-callout">
          <strong>One step left in Google Ads for the sheet actions</strong>
          <p>
            A new sheet needs a one-time Data Manager connection in its account: <em>Tools → Data Manager → Google Sheets</em>,
            paste the sheet link, choose the conversion action below, and map the columns. Or switch the action to direct delivery and skip this.
          </p>
          <ul>
            {created.map((s, i) => (
              <li key={i}>
                <span>{s.location ? `${s.location}, ` : ""}{s.action_name} in account {accountId(s.customer_id)}</span>
                <button className="button secondary" onClick={() => onCopy(s.sheet_url!, "Sheet link")}>Copy sheet link</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AddAction({ accounts, locations, busy, onSave }: {
  accounts: Account[];
  locations: { id: number; market_name: string }[];
  busy: boolean;
  onSave: (body: { action_type: ActionType; action_name: string; customer_id: string; location_id: number | null; conversion_action_id?: string; min_call_seconds?: number }) => Promise<void>;
}) {
  const [type, setType] = useState<ActionType>("booked");
  const [name, setName] = useState<string>(DEFAULT_NAME.booked);
  const [account, setAccount] = useState("");
  const [location, setLocation] = useState("");
  const [googleId, setGoogleId] = useState("");
  const [minSeconds, setMinSeconds] = useState("60");
  const locName = (id: number | null) => locations.find((l) => l.id === id)?.market_name;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    await onSave({
      action_type: type, action_name: name.trim(), customer_id: account, location_id: location ? Number(location) : null,
      ...(googleId.trim() ? { conversion_action_id: googleId.trim() } : {}),
      ...(type === "call" ? { min_call_seconds: Number(minSeconds) || 60 } : {}),
    });
  };
  return (
    <form className="callcenter-options-grid feed-add-grid" onSubmit={(e) => void submit(e)}>
      <label>
        Type
        <select value={type} onChange={(e) => {
          const t = e.target.value as ActionType;
          // The name follows the type until someone types their own.
          if (name === DEFAULT_NAME[type]) setName(DEFAULT_NAME[t]);
          setType(t);
        }}>
          <option value="booked">Booked: every appointment</option>
          <option value="completed">Completed: sends the job value</option>
          <option value="call">Qualified call: CTM calls on Google numbers</option>
        </select>
      </label>
      <label>
        Name in Google Ads
        <input value={name} onChange={(e) => setName(e.target.value)} required />
        <small>Must match the conversion action's name exactly.</small>
      </label>
      <label>
        Google Ads account
        <select value={account} required onChange={(e) => {
          setAccount(e.target.value);
          // An account linked to a territory fills it in.
          const acct = accounts.find((a) => a.customer_id === e.target.value);
          if (acct?.location_id) setLocation(String(acct.location_id));
        }}>
          <option value="">Choose an account</option>
          {accounts.map((a) => (
            <option key={a.customer_id} value={a.customer_id}>
              {a.account_label} ({accountId(a.customer_id)}){locName(a.location_id) ? `, ${locName(a.location_id)}` : ""}
            </option>
          ))}
        </select>
        {!accounts.length && <small>No Google Ads accounts linked. Link one in Setup → Connections first.</small>}
        {/\bLSA\b|local services/i.test(accounts.find((x) => x.customer_id === account)?.account_label || "") && (
          <small className="feed-warn">This looks like a Local Services Ads account. LSA does not take imported conversions; choose the Search account for this territory.</small>
        )}
      </label>
      <label>
        Territory
        <select value={location} onChange={(e) => setLocation(e.target.value)}>
          <option value="">Whole client</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.market_name}</option>)}
        </select>
        <small>Pick one when the export covers territories with their own Google Ads account.</small>
      </label>
      {type === "call" ? (
        <label>
          Minimum call length (seconds)
          <input type="number" min={10} max={600} value={minSeconds} onChange={(e) => setMinSeconds(e.target.value)} />
          <small>Shorter calls are not sent. Keep Google's own "Calls from ads" conversion off for these numbers, or they count twice.</small>
        </label>
      ) : (
        <label>
          Conversion action id in Google Ads
          <input value={googleId} onChange={(e) => setGoogleId(e.target.value)} placeholder="Leave blank for a sheet action" inputMode="numeric" />
          <small>The number at the end of the action's page address. With it, the action sends direct; without it, through a sheet.</small>
        </label>
      )}
      <p className="muted feed-add-help">
        Only needed for an account Connect did not cover. Connect creates "NL-AOS Booked" and "NL-AOS Completed" for you.
      </p>
      <div className="feed-add-submit">
        <button className="button" disabled={busy || !account || !name.trim()}>Add action</button>
      </div>
    </form>
  );
}
